import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { CustomScheduleVisit } from 'components/Assessments/components/VisitScheduling/types'
import { BaseSubsectionAdapter } from '../../../adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from '../../../adapters/types'
import { RocFormData } from '../../types/RocFormData'
import { RocFormField } from '../../types/RocFormField'
import { RocAssessmentQuestionnaireItem } from '../../types/questionnaire'
import {
  extractPeriodStartAndEndTimesWithTimezone,
  getAnswerValuesFromItems
} from '../../../helpers'
import {
  convertAppointmentTimeFormDataToFlatAnswers,
  convertTimeToUTCString
} from '../../../helpers.uncontrolled'
import {
  convertCustomScheduleToFlatAnswers,
  convertOtherAppointmentsToFlatAnswers,
  extractCustomSchedule,
  extractCustomScheduleByWeekday,
  extractOtherAppointments
} from '../../helpers/visitScheduling'

export class VisitSchedulingAdapter extends BaseSubsectionAdapter<RocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    options?: AdapterOptions
  ): Partial<RocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    const wakeupTime = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_WAKEUP_TIME
    )?.at(0)

    const bedtime = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_BEDTIME
    )?.at(0)

    const schoolTimes = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_DAY
    )

    const schoolTimesPeriod = extractPeriodStartAndEndTimesWithTimezone(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_PERIOD_START,
      RocAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_PERIOD_END,
      options?.caregiverTimezone
    )

    const otherAppointments = extractOtherAppointments(flatAnswersArray, options)
    const customSchedule = extractCustomSchedule(flatAnswersArray, options)
    const customScheduleByWeekday = extractCustomScheduleByWeekday(customSchedule)
    const editedCustomSchedule = []
    const additionalNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.VISIT_SCHEDULING_CUSTOM_SCHEDULE_ADDITIONAL_NOTES
    )?.at(0)
    const changeReason = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.VISIT_SCHEDULING_CUSTOM_SCHEDULE_CHANGE_REASON
    )?.at(0)

    return {
      [RocFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_WAKEUP_TIME]: wakeupTime,
      [RocFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_BEDTIME]: bedtime,
      [RocFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_DAY]: schoolTimes,
      [RocFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_PERIOD]: schoolTimesPeriod,
      [RocFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_OTHER_APPOINTMENTS]: otherAppointments,
      // Populate both daily and weekly visit schedule fields from FHIR
      // Daily mode uses flat array, weekly mode uses grouped by weekday
      [RocFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS]: customSchedule,
      [RocFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS_BY_WEEKDAY]: customScheduleByWeekday,
      [RocFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_EDITED_VISITS]: editedCustomSchedule,
      [RocFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_ADDITIONAL_NOTES]: additionalNotes,
      [RocFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_CHANGE_REASON]: changeReason
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(RocFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_WAKEUP_TIME)) {
      const wakeupTime = this.getFormDataField(
        RocFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_WAKEUP_TIME
      )
      const wakeUpTimeUTC = wakeupTime ? convertTimeToUTCString(wakeupTime) : undefined

      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_WAKEUP_TIME,
        value: wakeUpTimeUTC
      })
    }

    if (this.formDataFieldExists(RocFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_BEDTIME)) {
      const bedtime = this.getFormDataField(RocFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_BEDTIME)
      const bedtimeUTC = bedtime ? convertTimeToUTCString(bedtime) : undefined

      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_BEDTIME,
        value: bedtimeUTC
      })
    }

    if (this.formDataFieldExists(RocFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_DAY)) {
      const schoolTimesDay = this.getFormDataField(
        RocFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_DAY
      )

      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_DAY,
        value: schoolTimesDay
      })
    }

    if (
      this.formDataFieldExists(RocFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_PERIOD)
    ) {
      const schoolTimesPeriod = this.getFormDataField(
        RocFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_PERIOD
      )
      const { appointmentTimeValues } = convertAppointmentTimeFormDataToFlatAnswers(
        schoolTimesPeriod ?? [undefined, undefined]
      )

      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_PERIOD_START,
        value: appointmentTimeValues.at(0)
      })

      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_PERIOD_END,
        value: appointmentTimeValues.at(1)
      })
    }

    if (
      this.formDataFieldExists(RocFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_OTHER_APPOINTMENTS)
    ) {
      const otherAppointmentsField = this.getFormDataField(
        RocFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_OTHER_APPOINTMENTS
      )
      const otherAppointments = convertOtherAppointmentsToFlatAnswers(otherAppointmentsField)

      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_OTHER_APPOINTMENTS,
        value: otherAppointments
      })
    }

    // Handle both daily and weekly visit schedule modes
    // Weekly mode: visits grouped by weekday (preferred for weekly/shift scheduling)
    // Daily mode: flat array of visits (used for daily/visit scheduling)
    const visitsByWeekday = this.formDataFieldExists(
      RocFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS_BY_WEEKDAY
    )
      ? this.getFormDataField(RocFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS_BY_WEEKDAY)
      : undefined
    const dailyVisits = this.formDataFieldExists(
      RocFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS
    )
      ? this.getFormDataField(RocFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS)
      : undefined

    // Determine which source to use based on what has data
    // Prioritize visitsByWeekday if it has data, otherwise use dailyVisits
    let flatVisits: CustomScheduleVisit[] | undefined = undefined
    if (visitsByWeekday && visitsByWeekday.length > 0) {
      // Weekly mode: flatten the grouped structure
      flatVisits = visitsByWeekday.flatMap(({ visits }) => visits)
    } else if (dailyVisits && dailyVisits.length > 0) {
      // Daily mode: already flat
      flatVisits = dailyVisits
    }
    const customSchedule = flatVisits ? convertCustomScheduleToFlatAnswers(flatVisits) : undefined
    if (customSchedule) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS,
        value: customSchedule
      })
    }

    if (this.formDataFieldExists(RocFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_ADDITIONAL_NOTES)) {
      const additionalNotes = this.getFormDataField(
        RocFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_ADDITIONAL_NOTES
      )

      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.VISIT_SCHEDULING_CUSTOM_SCHEDULE_ADDITIONAL_NOTES,
        value: additionalNotes
      })
    }

    if (this.formDataFieldExists(RocFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_CHANGE_REASON)) {
      const changeReason = this.getFormDataField(
        RocFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_CHANGE_REASON
      )

      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.VISIT_SCHEDULING_CUSTOM_SCHEDULE_CHANGE_REASON,
        value: changeReason
      })
    }

    return mutations
  }
}
